import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  EnterPromoCodeRequest,
  Invoice,
  InvoiceRequest,
  UserInvoiceHistoryData,
} from '@/api';
import { calculatePricePerCheck } from '@/helpers';
import { ErrorCodeEnum } from '@/config';
import { PriceListPlansData } from '../models';

export interface PriceListPlansSliceState {
  paymentPlans: PriceListPlansData;
  paymentPlansLoading: boolean;
  paymentPlansError: string | null;
  enterPromoLoading: boolean;
  enterPromoError: string | null;
  enterPromoSuccess: boolean;
  stripeKey: string | null;
  cryptoInvoice: Invoice | null;
  paymentLoading: boolean;
  cryptoInvoiceError: string | null;
  discount: number;
  discountDueTime: number;
  invoiceHistory: UserInvoiceHistoryData | null;
  invoiceHistoryLoading: boolean;
  invoiceHistoryError: string | null;
  biggestPricePerCheck: number;
  errorCode: ErrorCodeEnum | null;
}

const initialState: PriceListPlansSliceState = {
  paymentPlans: null,
  paymentPlansLoading: false,
  paymentPlansError: null,
  enterPromoLoading: false,
  enterPromoError: null,
  enterPromoSuccess: false,
  stripeKey: null,
  cryptoInvoice: null,
  paymentLoading: false,
  cryptoInvoiceError: null,
  discount: 0,
  discountDueTime: 0,
  invoiceHistory: null,
  invoiceHistoryLoading: false,
  invoiceHistoryError: null,
  biggestPricePerCheck: 0,
  errorCode: null,
};

const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    setPaymentPlans: (state, action: PayloadAction<PriceListPlansData>) => ({
      ...state,
      paymentPlans: action.payload,
    }),
    setLoading: (state, action: PayloadAction<boolean>) => ({
      ...state,
      paymentPlansLoading: action.payload,
    }),
    setError: (state, action: PayloadAction<string | null>) => ({
      ...state,
      paymentPlansError: action.payload,
    }),
    setErrorCode: (state, action: PayloadAction<ErrorCodeEnum | null>) => ({
      ...state,
      errorCode: action.payload,
    }),
    fetchPriceListPlans: (state) => ({
      ...state,
      enterPromoError: null,
      paymentPlansLoading: true,
    }),
    enterPromoCode: (state, action: PayloadAction<EnterPromoCodeRequest>) => ({
      ...state,
      enterPromoError: null,
      enterPromoLoading: true,
    }),
    setEnterPromoLoading: (state, action: PayloadAction<boolean>) => ({
      ...state,
      enterPromoLoading: action.payload,
    }),
    setEnterPromoError: (state, action: PayloadAction<string | null>) => ({
      ...state,
      enterPromoError: action.payload,
    }),
    setEnterPromoSuccess: (state, action: PayloadAction<boolean>) => ({
      ...state,
      enterPromoSuccess: action.payload,
    }),
    createStripeInvoice: (state, action: PayloadAction<InvoiceRequest>) => ({
      ...state,
    }),
    setPaymentLoading: (state, action: PayloadAction<boolean>) => ({
      ...state,
      paymentLoading: action.payload,
    }),
    setStripeKey: (state, action: PayloadAction<string>) => ({
      ...state,
      stripeKey: action.payload,
    }),
    createCryptoInvoice: (state, action: PayloadAction<InvoiceRequest>) => ({
      ...state,
      paymentLoading: true,
    }),
    setCryptoInvoice: (state, action: PayloadAction<Invoice | null>) => ({
      ...state,
      cryptoInvoice: action.payload,
    }),
    setCryptoInvoiceError: (state, action: PayloadAction<string | null>) => ({
      ...state,
      cryptoInvoiceError: action.payload,
    }),
    setDiscounts: (
      state,
      action: PayloadAction<{
        discount: number;
        discountDueTime: number;
      }>,
    ) => ({
      ...state,
      discount: action.payload.discount,
      discountDueTime: action.payload.discountDueTime,
    }),
    fetchInvoiceHistory: (state) => ({
      ...state,
      invoiceHistoryLoading: true,
      invoiceHistoryError: null,
    }),
    setInvoiceHistoryLoading: (state, action: PayloadAction<boolean>) => ({
      ...state,
      invoiceHistoryLoading: action.payload,
    }),
    setInvoiceHistoryError: (state, action: PayloadAction<string | null>) => ({
      ...state,
      invoiceHistoryError: action.payload,
    }),
    setInvoiceHistory: (
      state,
      action: PayloadAction<UserInvoiceHistoryData>,
    ) => ({
      ...state,
      invoiceHistory: action.payload,
    }),
    setBiggestPricePerCheck: (
      state,
      action: PayloadAction<PriceListPlansData>,
    ) => ({
      ...state,
      biggestPricePerCheck: (action.payload!.plans || []).reduce(
        (acc: number, item) => {
          const pricePerCheck = calculatePricePerCheck(item);
          return Math.max(acc, pricePerCheck);
        },
        Number(action.payload?.baseUsdPrice) || 0,
      ),
    }),
  },
});
export { paymentSlice };
